import React, { useEffect, useState } from "react";
import { Edit, Trash2 } from "lucide-react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Apiurl from "../../environment/Apiurl";

const AddSalePharmacy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prescriptionId = location.state?.prescriptionId || null;
  const [showAddModal, setShowAddModal] = useState(false);
  const [responseData, setResponseData] = useState([]);
  const [responsePatientData, setResponsePatientData] = useState([]);
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const [medicineSearch, setMedicineSearch] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [showMedicineDropdown, setShowMedicineDropdown] = useState(false);
  const [isManualSaleEntry, setIsManualSaleEntry] = useState(false);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkedPrescription, setLinkedPrescription] = useState(null);
  const [isLoadingPrescription, setIsLoadingPrescription] = useState(false);
  const [prescriptionLoadError, setPrescriptionLoadError] = useState("");

  // Store the logged-in user on the sale so the same name can be shown
  // on both the generated invoice and future reprints.
  const billedByUser =
    localStorage.getItem("name") ||
    localStorage.getItem("user_name") ||
    localStorage.getItem("username") ||
    "Admin";

  const [generatedPatientId, setGeneratedPatientId] = useState("");
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [generateInvoiceNumberId, setGenerateInvoiceNumberId] = useState("");
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split("T")[0],
    invoice_number: "",
    patient_id: "",
    patient_name: "",
    mobile: "",
    doctor_name: "",
    discount_percentage: 0,
    discount_value: 0,
    apply_gst: true,
    gst_amount: 0,
    sub_total: 0,
    net_total: 0,
    round_off: 0,
    paid: 0,
    balance: 0,
    notes: "",
  });
  const [paymentData, setPaymentData] = useState({
    paid_date: new Date().toISOString().split("T")[0],
    pay_type: "",
    paid_amount: "",
  });
  const [paymentTableData, setPaymentTableData] = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState({
    stock_id: "",
    medicine_id: "",
    medicine_name: "",
    hsn_code: "",
    batch_no: "",
    balance_quantity: 0,
    quantity: 1,
    mrp: 0,
    gst_percentage: 0,
    cgst: 0,
    sgst: 0,
    expiry_date: "",
    unit: "",
    unit_pack: "",
  });
  const [selected_medicine, setselected_medicine] = useState({});
  // Keeps the medicine chosen from the linked prescription so its prescribed
  // price can be carried into the pharmacy sale Price field.
  const [selectedPrescriptionMedicine, setSelectedPrescriptionMedicine] = useState(null);

  // Fetch generated invoice ID
  const fetchGenerate = async () => {
    try {
      const response = await axios.get(`${Apiurl}/generate_sales_invoice_id`);
      const InvoiceNumberId = response.data.invoice_number;
      setGenerateInvoiceNumberId(InvoiceNumberId);
      setFormData((prev) => ({ ...prev, invoice_number: InvoiceNumberId }));
    } catch (error) {
      console.error(
        "Error fetching generated invoice_number ID:",
        error.message
      );
    }
  };

  // Fetch medicines from API
  const fetchResponse = async () => {
    try {
      const response = await axios.get(`${Apiurl}/stock_pharmacy`);
      console.log("greater Than 0: ", response.data);
      setResponseData(response.data || []);
    } catch (error) {
      console.error("Error fetching medicines:", error.message);
    }
  };

  // Fetch patients from API
  const fetchResponsePatient = async () => {
    setIsLoadingPatients(true);
    try {
      const response = await axios.get(`${Apiurl}/patient_details`);
      setResponsePatientData(response.data || []);
    } catch (error) {
      console.error("Error fetching patients:", error.message);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Resolve the doctor currently/latest consulted by the selected patient.
  // Active appointments are preferred; otherwise the latest appointment is used.
  const fetchDoctorForPatient = async (patientId) => {
    if (!patientId) return;

    setFormData((prev) => ({ ...prev, doctor_name: "" }));

    try {
      const response = await axios.get(`${Apiurl}/appointments`);
      const appointments = Array.isArray(response.data) ? response.data : [];

      const matchingAppointments = appointments.filter((appointment) =>
        (appointment?.patients_details || []).some(
          (patient) => String(patient?.patient_id || "") === String(patientId)
        )
      );

      const activeStatuses = new Set([
        "scheduled",
        "in progress",
        "rescheduled",
      ]);

      matchingAppointments.sort((a, b) => {
        const aStatus = String(a?.appointment_status || "").toLowerCase();
        const bStatus = String(b?.appointment_status || "").toLowerCase();
        const aActive = activeStatuses.has(aStatus) ? 1 : 0;
        const bActive = activeStatuses.has(bStatus) ? 1 : 0;

        if (aActive !== bActive) return bActive - aActive;

        const aDateTime = `${a?.appointment_date || ""} ${
          a?.appointment_time || ""
        }`;
        const bDateTime = `${b?.appointment_date || ""} ${
          b?.appointment_time || ""
        }`;
        return bDateTime.localeCompare(aDateTime);
      });

      const matchedAppointment = matchingAppointments[0];
      const doctorName =
        matchedAppointment?.doctor_details?.[0]?.doctor_name || "";

      setFormData((prev) => {
        if (String(prev.patient_id || "") !== String(patientId)) return prev;
        return {
          ...prev,
          doctor_name: doctorName || "N/A",
        };
      });
    } catch (error) {
      console.error("Error fetching patient doctor:", error);
      setFormData((prev) => {
        if (String(prev.patient_id || "") !== String(patientId)) return prev;
        return { ...prev, doctor_name: "N/A" };
      });
    }
  };

  // When opened from Pharmacy -> Prescription, load the exact prescription.
  // Patient is auto-filled; pharmacist still chooses batch, quantity and payment.
  const fetchPrescriptionForSale = async (id) => {
    if (!id) return;

    try {
      setIsLoadingPrescription(true);
      setPrescriptionLoadError("");

      const response = await axios.get(`${Apiurl}/prescriptions/${id}`);
      const prescription = response.data;
      const patient = prescription?.appointment?.patient_id;

      setLinkedPrescription(prescription || null);

      if (patient) {
        const fullName = `${patient.first_name || ""} ${patient.last_name || ""}`.trim();
        const department = (prescription?.appointment?.department || "").toLowerCase();
        const departmentPatientType = department.includes("yoga")
          ? "Yoga"
          : department.includes("homeo")
          ? "Homeopathy"
          : "";
        const inferredPatientType =
          patient.patient_type ||
          departmentPatientType ||
          (patient.patient_id?.startsWith("4HY-") ? "Yoga" : "Homeopathy");

        setFormData((prev) => ({
          ...prev,
          patient_type: inferredPatientType,
          patient_id: patient.patient_id || "",
          patient_name: fullName,
          mobile: patient.mobile_no || "",
          doctor_name:
            prescription?.appointment?.doctor_id?.doctor_name || "N/A",
        }));

        setPatientSearch(
          `${patient.patient_id || ""}${fullName ? ` - ${fullName}` : ""}`
        );
        setShowNewPatientForm(false);
        setIsNewPatient(false);
      }
    } catch (error) {
      console.error("Error fetching prescription for sale:", error);
      setPrescriptionLoadError(
        error.response?.data?.message || "Failed to load prescription"
      );
    } finally {
      setIsLoadingPrescription(false);
    }
  };

  useEffect(() => {
    fetchGenerate();
    fetchResponse();
    fetchResponsePatient();
  }, []);

  useEffect(() => {
    if (prescriptionId) {
      fetchPrescriptionForSale(prescriptionId);
    }
  }, [prescriptionId]);

  // Recalculate whenever selectedMedicines or paymentTableData changes
  useEffect(() => {
    setFormData((prev) => recalculateFormTotals(prev));
  }, [selectedMedicines, paymentTableData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
        [name]:
          name === "discount_percentage"
            ? Math.max(isNaN(parseInt(value)) ? 0 : parseInt(value), 0)
            : value,
      };
      if (name === "patient_type") {
        newData.patient_id = "";
        newData.patient_name = "";
        newData.mobile = "";
        newData.doctor_name = "";
        newData.patient_gender = "";
        setGeneratedPatientId("");
        setShowNewPatientForm(false);
        setPatientSearch("");
      }
      if (name === "discount_percentage" || name === "apply_gst") {
        return recalculateFormTotals(newData);
      }
      return newData;
    });
  };

  // Recalculate form totals
  const recalculateFormTotals = (formState) => {
    const subTotal = selectedMedicines.reduce(
      (sum, item) => sum + item.quantity * item.mrp - (item.cgst + item.sgst),
      0
    );
    const discountValue =
      (subTotal * parseFloat(formState.discount_percentage || 0)) / 100;
    const gstAmount = formState.apply_gst
      ? selectedMedicines.reduce(
          (sum, item) => sum + (item.cgst + item.sgst),
          0
        )
      : 0;
    const netTotal = subTotal - discountValue + gstAmount;
    const netTotalDecimal = netTotal % 1;
    let roundOff = 0;
    let roundedNetTotal = netTotal;
    if (netTotalDecimal >= 0.5) {
      roundedNetTotal = Math.ceil(netTotal);
      roundOff = roundedNetTotal - netTotal;
    } else {
      roundedNetTotal = Math.floor(netTotal);
      roundOff = roundedNetTotal - netTotal;
    }
    const paid = paymentTableData.reduce(
      (sum, item) => sum + item.paid_amount,
      0
    );
    const balance = roundedNetTotal - paid;

    return {
      ...formState,
      sub_total: subTotal,
      discount_value: discountValue,
      gst_amount: gstAmount,
      net_total: roundedNetTotal,
      round_off: roundOff,
      paid: paid,
      balance: balance,
    };
  };

  // Handle patient search and dropdown
  const handlePatientFocus = () => setShowPatientDropdown(true);
  const handlePatientBlur = () => {
    setTimeout(() => setShowPatientDropdown(false), 200);
  };

const selectPatient = (patient) => {
  const fullName = `${patient.first_name || ""} ${
    patient.last_name || ""
  }`.trim();

  setFormData((prev) => ({
    ...prev,
    patient_type: patient.patient_type || prev.patient_type,
    patient_id: patient.patient_id,
    patient_name: fullName,
    mobile: patient.mobile_no || "",
    doctor_name: "",
  }));

  setPatientSearch(`${patient.patient_id} - ${fullName}`);
  setShowPatientDropdown(false);

  fetchDoctorForPatient(patient.patient_id);
};

  // Doctors can prescribe multiple medicines in a single textarea entry,
  // separated by "+", e.g. "Arnica Montana - 2d  + Gelsemium-3d". For the
  // pharmacy sale/invoice, only the first medicine name segment is used.
  const getFirstMedicineName = (rawName) => {
    if (!rawName) return "";
    return String(rawName).split("+")[0].trim();
  };

  const handlePrescribedMedicineClick = (medicine) => {
    const firstMedicineName = getFirstMedicineName(medicine?.medicine_name);

    setSelectedPrescriptionMedicine(medicine || null);
    setMedicineSearch(firstMedicineName || "");
    setShowMedicineDropdown(true);

    // Keep the prescription price ready even before a batch is selected.
    // If the medicine has no stock match and is added manually, this same price
    // will be placed into the Price field.
    if (Number(medicine?.price || 0) > 0) {
      setSelectedMedicine((prev) => ({
        ...prev,
        mrp: Number(medicine.price),
      }));
    }

    setTimeout(() => {
      document.getElementById("sale-medicine-search")?.focus();
    }, 0);
  };

  // Handle medicine search and dropdown
  const handleMedicineFocus = () => setShowMedicineDropdown(true);
  const handleMedicineBlur = () => {
    setTimeout(() => setShowMedicineDropdown(false), 200);
  };

  const filteredMedicines = responseData.filter((item) => {
    const medicineName =
      item.medicine_details?.medicine_name ||
      item.medicine_details?.[0]?.medicine_name ||
      "";
    const batchNo = item.batch_no || "";
    const hsnCode =
      item.medicine_details?.hsn_code ||
      item.medicine_details?.[0]?.hsn_code ||
      "";
    const balanceQuantity = item.balance_quantity || 0;
    const searchText = `${medicineName} ${batchNo} ${hsnCode}`.toLowerCase();
    return (
      // searchText.includes(medicineSearch.toLowerCase()) && balanceQuantity > 0
      (searchText.includes(medicineSearch.toLowerCase()) &&
        balanceQuantity > 0) ||
      medicineName == "Consumables"
    );
  });

  const selectMedicine = (medicine) => {
    const medicineName =
      medicine.medicine_details?.medicine_name ||
      medicine.medicine_details?.[0]?.medicine_name ||
      "Unknown";
    const hsnCode =
      medicine.medicine_details?.hsn_code ||
      medicine.medicine_details?.[0]?.hsn_code ||
      "";
    const unitPack =
      medicine.medicine_details?.unit_pack ||
      medicine.medicine_details?.[0]?.unit_pack ||
      "";

    const isConsumables = medicineName === "Consumables";
    let initialQuantity = isConsumables ? "" : 1;
    const prescribedPrice = Number(selectedPrescriptionMedicine?.price || 0);
    let initialMrp = isConsumables
      ? ""
      : prescribedPrice > 0
      ? prescribedPrice
      : parseFloat(medicine.mrp) || 0;
    let initialGst = parseFloat(medicine.gst_percentage || 0);
    let initialCgst = 0;
    let initialSgst = 0;
    if (!isConsumables) {
      const basePrice = initialMrp / (1 + initialGst / 100);
      const taxAmountPerUnit = initialMrp - basePrice;
      const taxAmount = initialQuantity * taxAmountPerUnit;
      initialCgst = taxAmount / 2;
      initialSgst = taxAmount / 2;
    }

    setselected_medicine(medicine);
    setIsManualSaleEntry(false);

    setSelectedMedicine({
      stock_id: medicine._id,
      medicine_id: medicine.medicine_id,
      batch_no: medicine.batch_no || "",
      balance_quantity: medicine.balance_quantity || 0,
      quantity: initialQuantity,
      mrp: initialMrp,
      gst_percentage: initialGst,
      cgst: initialCgst,
      sgst: initialSgst,
      expiry_date: medicine.expiry_date || "",
      unit_pack: medicine.unit_pack,
      medicine_name: medicineName,
      hsn_code: hsnCode,
      unit: isConsumables ? "Other" : "",
    });
    setMedicineSearch(medicineName);
    setShowMedicineDropdown(false);
  };

  // Handle medicine input changes
  const handleMedicineInputChange = (e) => {
    const { name, value } = e.target;
    const isConsumables = selectedMedicine.medicine_name === "Consumables";
    const isEditablePrice = isConsumables || isManualSaleEntry;
    let mrp_rate = selected_medicine.mrp || 0;
    let quantity_multiplier = 1;

    if (
      !isConsumables &&
      !isManualSaleEntry &&
      name === "unit" &&
      (value === "Tablet" || value === "Bottle" || value === "Per Piece") &&
      selected_medicine.unit_pack?.includes("*")
    ) {
      const qty_per_unit = selected_medicine.unit_pack.split("*");
      if (qty_per_unit[1] && !isNaN(parseFloat(qty_per_unit[1]))) {
        mrp_rate =
          parseFloat(selected_medicine.mrp) / parseFloat(qty_per_unit[1]);
      }
    } else if (
      !isConsumables &&
      !isManualSaleEntry &&
      name === "unit" &&
      (value === "Strip" || value === "Pack") &&
      selected_medicine.unit_pack?.includes("*")
    ) {
      const qty_per_unit = selected_medicine.unit_pack.split("*");
      if (qty_per_unit[1] && !isNaN(parseFloat(qty_per_unit[1]))) {
        quantity_multiplier = parseFloat(qty_per_unit[1]);
      }
    }

    setSelectedMedicine((prev) => {
      let updatedMedicine = { ...prev };

      if (name === "quantity") {
        updatedMedicine[name] = parseInt(value) || 0;
      } else if (name === "mrp" && isEditablePrice) {
        updatedMedicine[name] = parseFloat(value) || 0;
      } else {
        updatedMedicine[name] = value;
      }

      if (name === "unit" && !isConsumables && !isManualSaleEntry) {
        updatedMedicine.mrp = parseFloat(mrp_rate);
        updatedMedicine.quantity_multiplier = quantity_multiplier;
      }

      // Recalculate tax-related fields
      const currentMrp = parseFloat(updatedMedicine.mrp) || 0;
      const currentGst = parseFloat(updatedMedicine.gst_percentage) || 0;
      const currentQty = parseInt(updatedMedicine.quantity) || 0;
      const base_price = currentMrp / (1 + currentGst / 100);
      const tax_amount_per_unit = currentMrp - base_price;
      const tax_amount = currentQty * tax_amount_per_unit;

      const finalMedicine = {
        ...updatedMedicine,
        cgst: tax_amount / 2,
        sgst: tax_amount / 2,
        total: currentQty * currentMrp,
      };
      return finalMedicine;
    });
  };

  // Add medicine to table
  const addMedicineToTable = () => {
    const qty = parseInt(selectedMedicine.quantity) || 0;
    if (!selectedMedicine.stock_id || qty < 1) {
      alert("Please select a medicine and enter a valid quantity.");
      return;
    }

    if (
      selectedMedicine.medicine_name === "Consumables" ||
      isManualSaleEntry
    ) {
      const mrpVal = parseFloat(selectedMedicine.mrp) || 0;
      if (mrpVal <= 0) {
        alert("Please enter a valid price.");
        return;
      }
    }

    // Check stock availability
    if (
      selectedMedicine.medicine_name !== "Consumables" &&
      !isManualSaleEntry &&
      qty > (selectedMedicine.balance_quantity || 0)
    ) {
      if (
        !window.confirm(
          `Warning: Requested quantity (${qty}) exceeds available stock (${selectedMedicine.balance_quantity}). This will result in negative stock. Proceed?`
        )
      ) {
        return;
      }
    }

    const existingMedicineIndex = selectedMedicines.findIndex(
      (item) =>
        item.medicine_id === selectedMedicine.medicine_id &&
        item.batch_no === selectedMedicine.batch_no &&
        item.unit === selectedMedicine.unit
    );

    let updatedMedicines;
    const mrpVal = parseFloat(selectedMedicine.mrp) || 0;
    if (existingMedicineIndex !== -1) {
      updatedMedicines = [...selectedMedicines];
      updatedMedicines[existingMedicineIndex].quantity += qty;
      const subtotal =
        updatedMedicines[existingMedicineIndex].quantity * mrpVal;
      const base_price =
        mrpVal /
        (1 + updatedMedicines[existingMedicineIndex].gst_percentage / 100);
      const tax_amount_per_unit = mrpVal - base_price;
      const tax_amount =
        updatedMedicines[existingMedicineIndex].quantity * tax_amount_per_unit;
      updatedMedicines[existingMedicineIndex].gst_percentage =
        updatedMedicines[existingMedicineIndex].gst_percentage;
      updatedMedicines[existingMedicineIndex].cgst = tax_amount / 2;
      updatedMedicines[existingMedicineIndex].sgst = tax_amount / 2;
      updatedMedicines[existingMedicineIndex].total = subtotal;
      updatedMedicines[existingMedicineIndex].unit_pack =
        selectedMedicine.unit_pack;
      updatedMedicines[existingMedicineIndex].quantity_multiplier =
        selectedMedicine.quantity_multiplier;
    } else {
      const subtotal = qty * mrpVal;
      const base_price = mrpVal / (1 + selectedMedicine.gst_percentage / 100);
      const tax_amount_per_unit = mrpVal - base_price;
      const tax_amount = qty * tax_amount_per_unit;
      updatedMedicines = [
        ...selectedMedicines,
        {
          stock_id: selectedMedicine.stock_id,
          medicine_id: selectedMedicine.medicine_id,
          medicine_name: selectedMedicine.medicine_name,
          batch_no: selectedMedicine.batch_no,
          expiry_date: selectedMedicine.expiry_date,
          hsn_code: selectedMedicine.hsn_code,
          quantity: qty,
          mrp: mrpVal,
          gst_percentage: selectedMedicine.gst_percentage,
          cgst: tax_amount / 2,
          sgst: tax_amount / 2,
          total: subtotal,
          unit: selectedMedicine.unit,
          unit_pack: selectedMedicine.unit_pack,
          quantity_multiplier: selectedMedicine.quantity_multiplier,
        },
      ];
    }

    setSelectedMedicines(updatedMedicines);

    setSelectedMedicine({
      stock_id: "",
      medicine_id: "",
      medicine_name: "",
      hsn_code: "",
      batch_no: "",
      balance_quantity: 0,
      quantity: 0,
      mrp: 0,
      gst_percentage: 0,
      cgst: 0,
      sgst: 0,
      expiry_date: "",
      unit: "",
      unit_pack: "",
      quantity_multiplier: 1,
    });
    setIsManualSaleEntry(false);
    setMedicineSearch("");
  };

  // Update medicine quantity
  const updateQuantity = (index, newQuantity) => {
    const parsedQuantity = parseInt(newQuantity) || 0;
    if (parsedQuantity < 1) return;

    const updatedMedicines = [...selectedMedicines];
    updatedMedicines[index].quantity = parsedQuantity;
    const subtotal = parsedQuantity * updatedMedicines[index].mrp;
    const base_price =
      updatedMedicines[index].mrp /
      (1 + updatedMedicines[index].gst_percentage / 100);
    const tax_amount_per_unit = updatedMedicines[index].mrp - base_price;
    const tax_amount = parsedQuantity * tax_amount_per_unit;
    updatedMedicines[index].cgst = tax_amount / 2;
    updatedMedicines[index].sgst = tax_amount / 2;
    updatedMedicines[index].total = subtotal;

    setSelectedMedicines(updatedMedicines);
  };

  // Remove medicine from table
  const removeMedicine = (index) => {
    const updatedMedicines = selectedMedicines.filter((_, i) => i !== index);
    setSelectedMedicines(updatedMedicines);
  };

  // Handle payment input changes
  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData((prev) => ({ ...prev, [name]: value }));
  };

  // Add payment to table
  const addPaymentToTable = () => {
    if (!paymentData.pay_type || !paymentData.paid_amount) {
      alert("Please fill in all payment fields.");
      return;
    }

    const paidAmount = parseFloat(paymentData.paid_amount) || 0;
    if (paidAmount <= 0) {
      alert("Paid amount must be greater than zero.");
      return;
    }

    const newPayment = {
      paid_date: paymentData.paid_date,
      pay_type: paymentData.pay_type,
      paid_amount: paidAmount,
    };

    setPaymentTableData((prev) => [...prev, newPayment]);

    setPaymentData({
      paid_date: new Date().toISOString().split("T")[0],
      pay_type: "",
      paid_amount: "",
    });
    setShowAddModal(false);
  };

  // Remove payment from table
  const removePaymentFromTable = (index) => {
    const updatedPayments = paymentTableData.filter((_, i) => i !== index);
    setPaymentTableData(updatedPayments);
  };

const filteredPatients = responsePatientData.filter((patient) => {
  const searchMatch =
    `${patient.patient_id} ${patient.first_name} ${patient.last_name} ${patient.mobile_no || ""}`
      .toLowerCase()
      .includes(patientSearch.toLowerCase());

  const patientType = String(patient.patient_type || "")
    .trim()
    .toLowerCase();

  let typeMatch = true;

  if (formData.patient_type === "Yoga") {
    typeMatch = patientType === "yoga";
  } else if (formData.patient_type === "Homeopathy") {
    typeMatch =
      patientType === "homeopathy" ||
      patientType === "homeo";
  }

  return searchMatch && typeMatch;
});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleGeneratePatientId = async () => {
    if (!formData.patient_type) {
      alert("Please select Patient Type first");
      return;
    }

    try {
      const generateUrl =
        formData.patient_type === "Yoga"
          ? `${Apiurl}/generate_patient_id`
          : `${Apiurl}/generate_homeo_id`;
      const res = await axios.get(generateUrl);
      const patient_id = res.data.patient_id;

      setGeneratedPatientId(patient_id);
      setFormData((prev) => ({
        ...prev,
        patient_id: patient_id,
        patient_name: "",
        mobile: "",
        doctor_name: "",
        patient_gender: "",
      }));
      setShowNewPatientForm(true);
      setIsNewPatient(true);
      setPatientSearch(patient_id);
    } catch (error) {
      console.error("Error generating patient ID:", error);
      alert("Failed to generate patient ID. Please try again.");
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      console.warn("Submission already in progress, ignoring click...");
      return;
    }

    if (
      !formData.invoice_number ||
      !formData.patient_id ||
      selectedMedicines.length === 0
    ) {
      alert(
        "Please fill in invoice, patient, and select at least one medicine."
      );
      return;
    }

    if (paymentTableData.length === 0) {
      alert("Please add payment details before submitting the sale.");
      return;
    }

    const derivedPaymentType = [
      ...new Set(paymentTableData.map((item) => item.pay_type).filter(Boolean)),
    ].join(" / ");

    setIsSubmitting(true); // 🔒 Lock submit

    try {
      if (isNewPatient) {
        const [first_name, ...lastNameParts] = formData.patient_name
          .trim()
          .split(" ");
        const last_name = lastNameParts.join(" ") || "";

        const payload = {
          patient_id: formData.patient_id,
          patient_type: formData.patient_type,
          first_name,
          last_name,
          mobile_no: formData.mobile,
          gender: formData.patient_gender,
        };

        await axios.post(`${Apiurl}/patient_details`, payload);
        await fetchResponsePatient();
        setIsNewPatient(false);
        setShowNewPatientForm(false);
        setGeneratedPatientId("");
        alert("New patient added successfully");
      }

      // ✅ Generate new invoice number
      const response = await axios.get(`${Apiurl}/generate_sales_invoice_id`);
      const newInvoiceNumber = response.data.invoice_number;

      const saleData = {
        sale_date: formData.sale_date,
        invoice_number: newInvoiceNumber,
        patient_id: formData.patient_id,
        patient_type: formData.patient_type || "",
        prescription_id: linkedPrescription?._id || null,
        // Backend also resolves this from prescription_id, so the workflow does not
        // depend on React navigation state/population shape.
        appointment_id:
          linkedPrescription?.appointment?._id ||
          linkedPrescription?.appointment ||
          null,
        gst_amount: formData.gst_amount,
        apply_gst: formData.apply_gst,
        sub_total: formData.sub_total,
        discount_percentage: formData.discount_percentage,
        discount_value: formData.discount_value,
        net_total: formData.net_total,
        round_off: formData.round_off,
        paid: formData.paid,
        balance: formData.balance,
        notes: formData.notes,
        payment_type: derivedPaymentType,
        created_by: billedByUser,
        sale_list: selectedMedicines.map((item) => ({
          invoice_number: newInvoiceNumber,
          medicine_id: item.medicine_id,
          medicine_name: item.medicine_name,
          batch_no: item.batch_no,
          hsn_code: item.hsn_code,
          quantity: item.quantity,
          expiry_date: item.expiry_date,
          mrp: item.mrp,
          tax: item.gst_percentage,
          cgst: item.cgst,
          sgst: item.sgst,
          total: item.total,
          unit: item.unit,
          unit_pack: item.unit_pack || "1*1",
        })),
      };

      const saleResponse = await axios.post(
        `${Apiurl}/sale_pharmacy`,
        saleData
      );

      if (saleResponse.status === 200) {
        if (paymentTableData.length > 0) {
          const paymentsData = {
            invoice_number: newInvoiceNumber,
            payments: paymentTableData,
          };
          await axios.post(`${Apiurl}/sale_payment_pharmacy`, paymentsData);
        }

        // Reset all fields
        setSelectedMedicines([]);
        setFormData({
          sale_date: new Date().toISOString().split("T")[0],
          invoice_number: "",
          patient_id: "",
          patient_name: "",
          mobile: "",
          doctor_name: "",
          discount_percentage: 0,
          discount_value: 0,
          apply_gst: true,
          gst_amount: 0,
          sub_total: 0,
          net_total: 0,
          round_off: 0,
          paid: 0,
          balance: 0,
          notes: "",
        });
        setPaymentTableData([]);
        setGeneratedPatientId("");

        const workflowCompleted =
          !linkedPrescription ||
          (saleResponse.data?.workflow?.billing_status === "Billed" &&
            saleResponse.data?.workflow?.appointment_status === "Completed");

        alert(
          linkedPrescription
            ? workflowCompleted
              ? "Sale submitted successfully! Prescription billed and appointment completed."
              : "Sale submitted, but workflow status could not be verified. Please refresh the appointment page."
            : "Sale submitted successfully!"
        );

        // Print window
        const printUrl = `/pharmacy/sale-pharmacy/add-sale-pharmacy/sale-bill/${newInvoiceNumber}`;
        const printWindow = window.open(printUrl, "_blank");
        if (!printWindow) {
          alert(
            "Popup blocked. Please allow popups for this site to view and print the bill."
          );
        }

        navigate("/pharmacy/sale-pharmacy");
      } else {
        alert("Sale submission failed. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting sale:", error);
      alert(
        `Failed to submit sale: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setTimeout(() => setIsSubmitting(false), 2000); // 🔓 Unlock after 2 sec
    }
  };

  const handleCancel = () => {
    navigate("/pharmacy/sale-pharmacy");
  };

  // Clear form
  const handleClearOverAll = async () => {
    try {
      // Fetch a new invoice number
      const response = await axios.get(`${Apiurl}/generate_sales_invoice_id`);
      const newInvoiceNumber = response.data.invoice_number;

      alert("Form cleared successfully");
      setSelectedMedicines([]);
      setFormData({
        sale_date: new Date().toISOString().split("T")[0],
        invoice_number: newInvoiceNumber,
        patient_id: "",
        patient_name: "",
        mobile: "",
        doctor_name: "",
        discount_percentage: 0,
        discount_value: 0,
        apply_gst: true,
        gst_amount: 0,
        sub_total: 0,
        net_total: 0,
        round_off: 0,
        paid: 0,
        balance: 0,
        notes: "",
      });
      setPatientSearch("");
      setPaymentTableData([]);
      setMedicineSearch("");
      setSelectedMedicine({
        stock_id: "",
        medicine_id: "",
        medicine_name: "",
        hsn_code: "",
        batch_no: "",
        balance_quantity: 0,
        quantity: 0,
        mrp: 0,
        gst_percentage: 0,
        cgst: 0,
        sgst: 0,
        expiry_date: "",
        unit: "",
        unit_pack: "",
        quantity_multiplier: 1,
      });
      setselected_medicine({});
      setGenerateInvoiceNumberId(newInvoiceNumber);
    } catch (error) {
      console.error("Error fetching new invoice number:", error);
      alert("Failed to fetch new invoice number. Please try again.");
    }
  };

  return (
    <>
      <div className="p-3">
        <div className="row">
          <div className="col-lg-9 col-xl-9">
            <div className="card">
              <div className="card-body py-1 px-3">
                <div className="row align-items-center">
                  <div className="col-md-2 col-lg-2 col-xl-2">
                    <label className="mb-1 fw-bold">Patient Type</label>
                    <select
                      className="form-control"
                      name="patient_type"
                      value={formData.patient_type}
                      onChange={handleInputChange}
                      disabled={Boolean(prescriptionId)}
                    >
                      <option value="">Select Type</option>
                      <option value="Homeopathy">Homeopathy</option>
                      <option value="Yoga">Yoga</option>
                    </select>
                  </div>

                  <div className="col-md-2 col-lg-2 col-xl-2">
                    <label htmlFor="patientId" className="mb-1 fw-bold">
                      Patient ID
                    </label>
                    <div className="position-relative">
                      <input
                        className="form-control"
                        type="text"
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                        onFocus={handlePatientFocus}
                        onBlur={handlePatientBlur}
                        placeholder="Patient ID"
                        autoComplete="off"
                        disabled={Boolean(prescriptionId)}
                      />
                      <i className="bi bi-caret-down-fill position-absolute end-0 top-50 translate-middle-y me-3"></i>
                      {showPatientDropdown && !showNewPatientForm && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            zIndex: 1000,
                            width: "100%",
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: "white",
                            border: "1px solid #ddd",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        >
                          {isLoadingPatients ? (
                            <div
                              style={{ padding: "8px", textAlign: "center" }}
                            >
                              Loading...
                            </div>
                          ) : filteredPatients.length > 0 ? (
                            filteredPatients.map((patient, index) => (
                              <div
                                key={patient.patient_id || index}
                                style={{
                                  padding: "8px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                  backgroundColor: "#fff",
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  selectPatient(patient);
                                }}
                              >
                                {patient.patient_id} - {patient.first_name}{" "}
                                {patient.last_name || ""}
                                <div
                                  style={{ fontSize: "0.8rem", color: "#666" }}
                                >
                                  {patient.mobile_no}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div
                              style={{
                                padding: "8px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              No Matching Patients Found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {!prescriptionId && (
                    <div className="col-md-1 col-lg-1 col-xl-1 d-flex align-items-start">
                      <button
                        className="btn btn-primary"
                        onClick={handleGeneratePatientId}
                      >
                        +
                      </button>
                    </div>
                  )}

                  <div className="col-md-3 col-lg-3 col-xl-3">
                    <label className="mb-1 fw-bold">Patient Name</label>
                    <input
                      className="form-control"
                      type="text"
                      name="patient_name"
                      value={formData.patient_name}
                      onChange={handleInputChange}
                      placeholder={
                        showNewPatientForm
                          ? "Enter Full Name"
                          : "Enter Patient Name"
                      }
                      readOnly={!showNewPatientForm}
                    />
                  </div>
                  <div className="col-md-2 col-lg-2 col-xl-2">
                    <label className="mb-1 fw-bold">Patient Mobile</label>
                    <input
                      className="form-control"
                      type="text"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      placeholder={
                        showNewPatientForm ? "Mobile Number" : "Patient Mobile"
                      }
                      readOnly={!showNewPatientForm}
                    />
                  </div>
                  <div className="col-md-2 col-lg-2 col-xl-2">
                    <label className="mb-1 fw-bold">Doctor Name</label>
                    <input
                      className="form-control"
                      type="text"
                      name="doctor_name"
                      value={formData.doctor_name || ""}
                      placeholder="Doctor Name"
                      readOnly
                    />
                  </div>
                  {showNewPatientForm && (
                    <div className="col-md-2 col-lg-2 col-xl-2">
                      <label className="mb-1 fw-bold">Gender</label>
                      <select
                        className="form-control"
                        name="patient_gender"
                        value={formData.patient_gender}
                        onChange={handleInputChange}
                        disabled={!showNewPatientForm}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-xl-3">
            <div className="card">
              <div className="card-body py-1 px-3">
                <div className="row g-2">
                  <div className="col-md-6 col-lg-6 col-xl-6">
                    <label className="mb-1 fw-bold">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="sale_date"
                      value={formData.sale_date}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6 col-lg-6 col-xl-6">
                    <label className="mb-1 fw-bold">Invoice Number</label>
                    <input
                      className="form-control"
                      type="text"
                      name="invoice_number"
                      value={formData.invoice_number}
                      readOnly
                      placeholder="Invoice Number"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {prescriptionId && (
        <div className="px-3 mb-2">
          <div className="card border-warning">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Prescription Medicines</strong>
              <span className="badge bg-warning text-dark">
                {linkedPrescription?.billing_status || "Pending"}
              </span>
            </div>
            <div className="card-body py-2">
              {isLoadingPrescription ? (
                <div className="text-muted">Loading prescription...</div>
              ) : prescriptionLoadError ? (
                <div className="alert alert-danger mb-0">{prescriptionLoadError}</div>
              ) : linkedPrescription?.medicines?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Medicine</th>
                        <th>Price</th>
                        <th>Duration</th>
                        <th>Dose Type</th>
                        <th>Administration</th>
                        <th>Batch / Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedPrescription.medicines.map((medicine, index) => (
                        <tr key={medicine._id || `${medicine.medicine_name}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{medicine.medicine_name || "N/A"}</td>
                          <td>
                            {Number(medicine.price || 0) > 0
                              ? `₹${Number(medicine.price).toFixed(2)}`
                              : "-"}
                          </td>
                          <td>{medicine.duration || "N/A"}</td>
                          <td>
                            {(medicine.dose_type || medicine?.timing?.fn?.dose_type || "drops")
                              .charAt(0)
                              .toUpperCase() +
                              (medicine.dose_type || medicine?.timing?.fn?.dose_type || "drops").slice(1)}
                          </td>
                          <td>{medicine.water ? "With Water" : "Direct"}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                handlePrescribedMedicineClick(medicine)
                              }
                            >
                              Choose Batch / Qty
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-muted">No medicines found in this prescription.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="px-3">
        <div className="row">
          <div className="col-md-8 col-lg-9 col-xl-9">
            <div className="border rounded p-3 bg-white">
              <div className="row">
                <div className="col-md-4 col-lg-4 col-xl-4 mb-2">
                  <label className="form-label fw-bold">Medicine Name</label>
                  <div className="position-relative">
                    <input
                      id="sale-medicine-search"
                      type="text"
                      className="form-control"
                      value={medicineSearch}
                      onChange={(e) => setMedicineSearch(e.target.value)}
                      onFocus={handleMedicineFocus}
                      onBlur={handleMedicineBlur}
                      placeholder="Search Medicine, Batch, or HSN"
                    />
                    <i className="bi bi-caret-down-fill position-absolute end-0 top-50 translate-middle-y me-3"></i>
                    {showMedicineDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          zIndex: 1000,
                          width: "100%",
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                      >
                        {filteredMedicines.length > 0 ? (
                          filteredMedicines.map((medicine, index) => (
                            <div
                              key={medicine._id || index}
                              style={{
                                padding: "8px",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                                backgroundColor: "#fff",
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                selectMedicine(medicine);
                              }}
                            >
                              {medicine.medicine_details?.medicine_name ||
                                medicine.medicine_details?.[0]?.medicine_name}
                              <div
                                style={{ fontSize: "0.8rem", color: "#666" }}
                              >
                                HSN Code: {medicine.medicine_details?.hsn_code}{" "}
                                | Batch: {medicine.batch_no} | Pack:{" "}
                                {medicine.unit_pack} | Expiry:{" "}
                                {medicine.expiry_date} | Avl:{" "}
                                {medicine.balance_quantity} | Rs.{" "}
                                {parseFloat(medicine.mrp).toFixed(2)} | GST:{" "}
                                {medicine.gst_percentage || "0"}%
                              </div>
                            </div>
                          ))
                        ) : (
                          <div
                            style={{
                              padding: "8px",
                              cursor: medicineSearch.trim() ? "pointer" : "default",
                              color: "#666",
                              fontStyle: "italic",
                            }}
                            onMouseDown={(e) => {
                              if (!medicineSearch.trim()) return;
                              e.preventDefault();
                              const typedName = medicineSearch.trim();
                              const manualId = `manual-${Date.now()}`;
                              setselected_medicine({});
                              setIsManualSaleEntry(true);
                              setSelectedMedicine({
                                stock_id: manualId,
                                medicine_id: manualId,
                                batch_no: "",
                                balance_quantity: 0,
                                quantity: 1,
                                mrp:
                                  Number(selectedPrescriptionMedicine?.price || 0) > 0
                                    ? Number(selectedPrescriptionMedicine.price)
                                    : "",
                                gst_percentage: 0,
                                cgst: 0,
                                sgst: 0,
                                expiry_date: "",
                                unit_pack: "",
                                medicine_name: typedName,
                                hsn_code: "",
                                unit: "",
                                quantity_multiplier: 1,
                              });
                              setMedicineSearch(typedName);
                              setShowMedicineDropdown(false);
                            }}
                          >
                            {medicineSearch.trim() ? (
                              <>No Matching Medicines Found — click to add{" "}
                                <strong>"{medicineSearch.trim()}"</strong>{" "}
                                manually with your own price</>
                            ) : (
                              "No Matching Medicines Found"
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-2 col-lg-2 col-xl-2 mb-2">
                  <label className="form-label fw-bold">Qty</label>
                  <input
                    type="number"
                    className="form-control"
                    name="quantity"
                    value={selectedMedicine.quantity}
                    onChange={handleMedicineInputChange}
                    placeholder="Quantity"
                  />
                </div>

                <div className="col-md-2 col-lg-2 col-xl-2 mb-2">
                  <label className="form-label fw-bold">Unit</label>
                  <select
                    className="form-control"
                    name="unit"
                    value={selectedMedicine.unit}
                    onChange={handleMedicineInputChange}
                  >
                    <option value="">Select Unit</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Pack">Pack</option>
                    <option value="Per Piece">Per Piece</option>
                    <option value="Bottle">Bottle</option>
                    <option value="Strip">Strip</option>
                    {(selectedMedicine.medicine_name === "Consumables" ||
                      isManualSaleEntry) && <option value="Other">Other</option>}
                  </select>
                </div>

                <div className="col-md-2 col-lg-2 col-xl-2 mb-2">
                  <label className="form-label fw-bold">Price</label>
                  <input
                    type="text"
                    className="form-control"
                    name="mrp"
                    value={
                      selectedMedicine.medicine_name === "Consumables" ||
                      isManualSaleEntry
                        ? selectedMedicine.mrp || ""
                        : selectedMedicine.mrp
                        ? parseFloat(selectedMedicine.mrp).toFixed(2)
                        : ""
                    }
                    readOnly={
                      selectedMedicine.medicine_name !== "Consumables" &&
                      !isManualSaleEntry
                    }
                    onChange={handleMedicineInputChange}
                  />
                </div>

                <div className="col-md-1 col-lg-1 col-xl-1 mt-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-success w-100"
                    onClick={addMedicineToTable}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="table-responsive table-scroll-350">
                <table className="table table-bordered mb-0">
                  <thead className="text-center">
                    <tr>
                      {[
                        "S.no",
                        "Medicine Name",
                        "Qty",
                        "Unit",
                        "Price",
                        "GST %",
                        "Amount",
                        "Action",
                      ].map((h, idx) => (
                        <th
                          key={idx}
                          style={{
                            backgroundColor: "#01AEBE",
                            color: "white",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {selectedMedicines.length > 0 ? (
                      selectedMedicines.map((item, index) => (
                        <tr key={item.stock_id || index}>
                          <td>{index + 1}</td>
                          <td>{item.medicine_name}</td>
                          <td>
                            <div className="d-flex justify-content-center gap-1">
                              <input
                                type="number"
                                className="form-control form-control-sm text-center"
                                style={{ width: "60px" }}
                                value={item.quantity}
                                onChange={(e) =>
                                  updateQuantity(
                                    index,
                                    parseInt(e.target.value) || 1
                                  )
                                }
                              />
                            </div>
                          </td>
                          <td>{item.unit || "N/A"}</td>
                          <td>{item.mrp.toFixed(2)}</td>
                          <td>{item.gst_percentage}%</td>
                          <td>{item.total.toFixed(2)}</td>
                          <td className="d-flex justify-content-center gap-1">
                            <button
                              className="btn btn-sm p-1 border-0"
                              style={{ backgroundColor: "transparent" }}
                              onClick={() => {
                                setSelectedMedicine({
                                  stock_id: item.stock_id,
                                  medicine_id: item.medicine_id,
                                  medicine_name: item.medicine_name,
                                  hsn_code: item.hsn_code,
                                  batch_no: item.batch_no,
                                  balance_quantity:
                                    responseData.find(
                                      (m) => m._id === item.stock_id
                                    )?.balance_quantity || 0,
                                  quantity: item.quantity,
                                  mrp: item.mrp,
                                  gst_percentage: item.gst_percentage,
                                  cgst: item.cgst,
                                  sgst: item.sgst,
                                  expiry_date: item.expiry_date,
                                  unit: item.unit,
                                  unit_pack: item.unit_pack,
                                  quantity_multiplier: item.quantity_multiplier,
                                });
                                setMedicineSearch(item.medicine_name);
                              }}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                            <button
                              className="btn btn-sm p-1 border-0"
                              style={{ backgroundColor: "transparent" }}
                              onClick={() => removeMedicine(index)}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No medicines selected
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

              {paymentTableData.length > 0 && (
                <div className="border rounded bg-white mt-3">
                  <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="mb-0 fw-bold">Payment Details</h6>
                    <span className="badge bg-light text-dark border">
                      {paymentTableData.length} Payment
                      {paymentTableData.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <thead className="text-center">
                        <tr>
                          {["S.No", "Paid Date", "Pay Type", "Amount", "Action"].map(
                            (heading) => (
                              <th
                                key={heading}
                                style={{
                                  backgroundColor: "#34b4bd",
                                  color: "white",
                                }}
                              >
                                {heading}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody className="text-center">
                        {paymentTableData.map((item, index) => (
                          <tr key={`${item.paid_date}-${item.pay_type}-${index}`}>
                            <td>{index + 1}</td>
                            <td>{item.paid_date}</td>
                            <td>{item.pay_type}</td>
                            <td>₹ {Number(item.paid_amount || 0).toFixed(2)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm p-1 border-0"
                                style={{ backgroundColor: "transparent" }}
                                onClick={() => removePaymentFromTable(index)}
                                title="Remove payment"
                              >
                                <Trash2 size={16} className="text-danger" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </div>

          <div className="col-md-4 col-lg-3 col-xl-3">
            <div className="row card card-body">
              <div className="row">
                <div className="col-5 col-md-5 col-lg-5 col-xl-5">
                  <label className="text-dark fw-bold">Sub Total</label>
                </div>
                <div className="col-7 col-md-7 col-lg-7 col-xl-7">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.sub_total.toFixed(2)}
                    readOnly
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-5 col-md-5 col-lg-5 col-xl-5">
                  <label className="fw-bold">Discount</label>
                </div>
                <div className="col-7 col-md-7 col-lg-7 col-xl-7">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    name="discount_percentage"
                    value={formData.discount_percentage}
                    onChange={handleInputChange}
                    placeholder="Discount %"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-5 col-md-5 col-lg-5 col-xl-5">
                  <label className="fw-bold">Tax</label>
                </div>
                <div className="col-7 col-md-7 col-lg-7 col-xl-7">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.gst_amount.toFixed(2)}
                    readOnly
                    placeholder="Tax Amount"
                  />
                </div>
              </div>

              <div  style={{ backgroundColor: "#01AEBE" }}>
                <div className="row pt-3 pb-1 p-2">
                  <label className="col-5 fs-5 col-form-label pt-3 text-white fw-bold">
                    Net Total
                  </label>
                  <div className="col-7">
                    <input
                      type="text"
                      className="form-control text-end fs-4 fw-bold text-primary"
                      value={formData.net_total.toFixed(2)}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-2 mt-2">
                <button
                  type="button"
                  className="btn btn-lg fw-bold btn-success fs-5 w-100"
                  onClick={() => setShowAddModal(true)}
                >
                  Pay
                </button>
              </div>

              <div className="row mt-2">
                <div className="col-5 col-md-5 col-lg-5 col-xl-5">
                  <label className="fw-bold">Paid</label>
                </div>
                <div className="col-7 col-md-7 col-lg-7 col-xl-7">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.paid.toFixed(2)}
                    readOnly
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-5 col-md-5 col-lg-5 col-xl-5">
                  <label className="fw-bold">Balance</label>
                </div>
                <div className="col-7 col-md-7 col-lg-7 col-xl-7">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.balance.toFixed(2)}
                    readOnly
                  />
                </div>
              </div>

              <div className="row text-center">
                <div className="col-4">
                  <button
                    className="btn btn-sm btn-secondary w-100"
                    onClick={handleClearOverAll}
                  >
                    Clear
                  </button>
                </div>
                <div className="col-4">
                  <button
                    className="btn btn-sm btn-success w-100"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
                <div className="col-4">
                  <button
                    className="btn btn-sm btn-danger w-100"
                    onClick={handleCancel}
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showAddModal ? "show d-block" : ""}`}
        tabIndex="-1"
        aria-labelledby="addPaymentModalLabel"
        aria-hidden={!showAddModal}
        style={{
          backgroundColor: "rgba(0,0,0,0.5)",
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold" id="addPaymentModalLabel">
                Add Payment Details
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowAddModal(false)}
              ></button>
            </div>

            <div className="modal-body py-2">
  <div className="row g-2 align-items-end">

    {/* Date */}
    <div className="col">
      <label className="form-label fw-bold mb-1">Date</label>
      <input
        type="date"
        className="form-control form-control-sm"
        name="paid_date"
        value={paymentData.paid_date}
        onChange={handlePaymentInputChange}
      />
    </div>

    {/* Total Amount */}
    <div className="col">
      <label className="form-label fw-bold mb-1">Total</label>
      <input
        type="text"
        className="form-control form-control-sm"
        value={formData.net_total.toFixed(2)}
        readOnly
      />
    </div>

    {/* Payment Type */}
    <div className="col">
      <label className="form-label fw-bold mb-1">Payment</label>
      <select
        className="form-control form-control-sm"
        name="pay_type"
        value={paymentData.pay_type}
        onChange={handlePaymentInputChange}
      >
        <option value="">Select</option>
        <option value="Cash">Cash</option>
        <option value="Card">Card</option>
        <option value="Gpay">Gpay</option>
        <option value="Phonepe">Phonepe</option>
        <option value="Paytm">Paytm</option>
        <option value="Amazon Pay">Amazon Pay</option>
        <option value="UPI">UPI</option>
      </select>
    </div>

    {/* Paid Amount */}
    <div className="col">
      <label className="form-label fw-bold mb-1">Paid</label>
      <input
        type="number"
        className="form-control form-control-sm"
        name="paid_amount"
        value={paymentData.paid_amount}
        onChange={handlePaymentInputChange}
        placeholder="Amount"
      />
    </div>

    {/* Balance */}
    <div className="col">
      <label className="form-label fw-bold mb-1">Balance</label>
      <input
        type="text"
        className="form-control form-control-sm"
        value={formData.balance.toFixed(2)}
        readOnly
      />
    </div>

    {/* Add */}
    <div className="col-auto">
      <button
        type="button"
        className="btn btn-sm btn-success px-3"
        onClick={addPaymentToTable}
      >
        ADD
      </button>
    </div>

  </div>
</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddSalePharmacy;